import _define_property from "@swc/helpers/src/_define_property.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { addYears } from "date-fns/addYears";
import { format } from "date-fns/format";
import { useEffect, useState } from "react";
import Close from "@engineering/icons/close";
import { BodyBoldText, Button, datetime, String, Ui } from "@silvertours/front-shared";
import { DatePicker } from "./DatePicker";
import { TimePicker } from "./TimePicker";
import { useLocale } from "../Runtime";
import { CalendarIcon, DatePickerButton, DatePickerValue, DatePickerValues, DateSeparator, TopBarHeadline } from "./DateRangePicker.styles";
var HOUR_IN_MS = 1000 * 60 * 60;
var DateRangePicker = function(_param) {
    var from = _param.from, to = _param.to, _open = _param.open, open = _open === void 0 ? false : _open, _vertical = _param.vertical, vertical = _vertical === void 0 ? false : _vertical, error = _param.error, onDatesChange = _param.onDatesChange, className = _param.className, translations = _object_without_properties(_param, [
        "from",
        "to",
        "open",
        "vertical",
        "error",
        "onDatesChange",
        "className"
    ]);
    var ref;
    var language = useLocale().language;
    var dateLocale = datetime.dateLocales[language];
    var localDateFormats = String.dateFormats[language];
    var ref1 = useState(open), isCalendarVisible = ref1[0], setCalendarVisibility = ref1[1];
    var ref2 = useState({
        from: from,
        to: to
    }), selectedDates = ref2[0], setSelectedDates = ref2[1];
    useEffect(function() {
        setSelectedDates({
            from: from,
            to: to
        });
    }, [
        from,
        to
    ]);
    var handleBackdropClick = function(event) {
        event.stopPropagation();
        setCalendarVisibility(false);
    };
    var handleOnDatesChange = function(dateRange) {
        var newDateRange = {
            from: new Date(),
            to: new Date()
        };
        if (dateRange.from && dateRange.to && selectedDates.from && selectedDates.to) {
            newDateRange = {
                from: datetime.addTimeToDate(dateRange.from, selectedDates.from.getHours(), selectedDates.from.getMinutes()),
                to: datetime.addTimeToDate(dateRange.to, selectedDates.to.getHours(), selectedDates.to.getMinutes())
            };
            if (format(dateRange.from, String.isoDateTime).split("T")[0] === format(dateRange.to, String.isoDateTime).split("T")[0]) {
                newDateRange = {
                    from: datetime.addTimeToDate(dateRange.from, 10, 0),
                    to: datetime.addTimeToDate(dateRange.to, 17, 0)
                };
            }
            if (typeof onDatesChange !== "undefined") {
                onDatesChange(newDateRange);
            }
            setSelectedDates(newDateRange);
        }
    };
    var handleOnTimeChange = function(time, type) {
        var ref = _sliced_to_array(time.split(":"), 2), hours = ref[0], minutes = ref[1];
        if (selectedDates[type]) {
            var dateWithTime = datetime.addTimeToDate(selectedDates[type], Number(hours), Number(minutes));
            var newDateRange = _object_spread_props(_object_spread({}, selectedDates), _define_property({}, type, dateWithTime));
            if (typeof onDatesChange !== "undefined") {
                onDatesChange(newDateRange);
            }
            setSelectedDates(newDateRange);
        }
    };
    var getDays = function() {
        if (selectedDates.from && selectedDates.to) {
            // Inspired by https://youmightnotneed.com/date-fns#differenceInDays,
            // because differenceInHours() from date-fns always rounds which is not what we want
            var days = (selectedDates.to - selectedDates.from) / HOUR_IN_MS / 24;
            return Number.isInteger(days) ? days : Math.ceil(days);
        }
        return 1;
    };
    // calendar range: current month up to two years
    var now = new Date();
    var calendarRange = {
        start: now,
        end: addYears(now, 2)
    };
    // scrolling shadow effect
    var shadowEffect = vertical ? {
        background: "\n      linear-gradient(white 30%, rgba(255, 255, 255, 0)),\n      linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,\n      radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)),\n      radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0)) 0 100%",
        backgroundRepeat: "no-repeat",
        backgroundColor: "white",
        backgroundSize: "100% 40px, 100% 40px, 100% 14px, 100% 14px",
        backgroundAttachment: "local, local, scroll, scroll"
    } : undefined;
    var renderDate = function(date) {
        return date && format(date, vertical ? localDateFormats.dayTimeShort : localDateFormats.dayTimeLong, {
            locale: dateLocale
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: clsx("relative", className),
        children: [
            isCalendarVisible && // OnKeyDown is necessary for accessibility reasons
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            /*#__PURE__*/ _jsx("div", {
                className: "fixed bottom-0 left-0 right-0 top-0 select-none bg-transparent",
                onClick: handleBackdropClick,
                "data-testid": "date-picker-backdrop"
            }),
            /*#__PURE__*/ _jsxs(DatePickerButton, {
                type: "button",
                id: "datepicker",
                onClick: function() {
                    return setCalendarVisibility(true);
                },
                isInvalid: !!error,
                children: [
                    /*#__PURE__*/ _jsx(CalendarIcon, {
                        id: "datepicker-cal-icon"
                    }),
                    /*#__PURE__*/ _jsxs(DatePickerValues, {
                        id: "datepickerValue",
                        children: [
                            /*#__PURE__*/ _jsx(DatePickerValue, {
                                children: renderDate(selectedDates.from)
                            }),
                            /*#__PURE__*/ _jsx(DateSeparator, {}),
                            /*#__PURE__*/ _jsx(DatePickerValue, {
                                children: renderDate(selectedDates.to)
                            })
                        ]
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx(Ui.ErrorMessage, {
                children: error
            }),
            isCalendarVisible && /*#__PURE__*/ _jsxs("div", {
                className: "md:border-color8 fixed bottom-0 left-0 right-0 top-0 z-50 flex flex-col rounded-md bg-white md:absolute md:bottom-auto md:right-auto md:top-auto md:mt-10 md:block md:w-[688px] md:overflow-hidden md:border-2",
                children: [
                    vertical && /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-row items-center justify-between bg-white p-40",
                        children: [
                            /*#__PURE__*/ _jsx(TopBarHeadline, {
                                children: translations.topBarHeadline
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "aria-label": translations.close,
                                onClick: function() {
                                    return setCalendarVisibility(false);
                                },
                                children: /*#__PURE__*/ _jsx(Close, {
                                    className: "fill-primary h-60 w-60"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "w-auto flex-grow overflow-y-scroll md:overflow-hidden",
                        style: shadowEffect,
                        "data-testid": "date-picker",
                        children: /*#__PURE__*/ _jsx(DatePicker, {
                            onDatesChange: handleOnDatesChange,
                            disableNavigation: vertical,
                            disabled: {
                                before: new Date()
                            },
                            numberOfMonths: vertical ? 15 : 2,
                            fromMonth: calendarRange.start,
                            toMonth: calendarRange.end,
                            from: selectedDates.from ? selectedDates.from : undefined,
                            to: selectedDates.to ? selectedDates.to : undefined,
                            locale: dateLocale,
                            defaultMonth: (vertical ? undefined : selectedDates.from) || undefined,
                            scrollToSelectedMonth: vertical
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-color5 md:p-50 flex flex-col items-center justify-between gap-40 p-40 md:flex-row",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-between gap-60 md:justify-start md:gap-40",
                                children: [
                                    /*#__PURE__*/ _jsx(TimePicker, {
                                        type: "from",
                                        onChange: handleOnTimeChange,
                                        returnTimeLabel: translations.returnTimeLabel,
                                        returnTimeText: translations.returnTimeText,
                                        pickupTimeLabel: translations.pickupTimeLabel,
                                        pickupTimeText: translations.pickupTimeText,
                                        value: selectedDates.from ? format(selectedDates.from, localDateFormats.time) : undefined
                                    }),
                                    /*#__PURE__*/ _jsx(TimePicker, {
                                        type: "to",
                                        onChange: handleOnTimeChange,
                                        returnTimeLabel: translations.returnTimeLabel,
                                        returnTimeText: translations.returnTimeText,
                                        pickupTimeLabel: translations.pickupTimeLabel,
                                        pickupTimeText: translations.pickupTimeText,
                                        value: selectedDates.to ? format(selectedDates.to, localDateFormats.time) : undefined
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                className: "w-full md:w-auto",
                                size: "small",
                                type: "button",
                                variant: "primary",
                                onClick: function() {
                                    return setCalendarVisibility(false);
                                },
                                children: /*#__PURE__*/ _jsx(BodyBoldText, {
                                    inline: true,
                                    textColor: "white",
                                    children: (ref = translations.confirmButtonText) === null || ref === void 0 ? void 0 : ref.replace("{{days}}", getDays().toString())
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export { DateRangePicker };
